<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {
  PUTAWAY_STATUS_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  cancelAsn,
  closeAsn,
  confirmPutaway,
  getAsn,
  receiveAsn,
  releaseAsn,
  type AsnDetail,
  type InboundStatus,
  type PutawayDetail,
  type PutawayStatus,
} from '../api/inbound'
import { listLocations, type Location } from '../api/locations'

const route = useRoute()
const router = useRouter()

const detail = ref<AsnDetail | null>(null)
const locations = ref<Location[]>([])
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const busy = ref(false)

/** 收货弹窗里的一行：只收 remaining 以内的数量 */
interface ReceiveLine {
  asn_item_id: number
  sku_label: string
  unit: string
  batch_managed: boolean
  remaining: number
  qty: number
  batch_no: string
  production_date: string | null
  expiry_date: string | null
  location_id: number
}

const receiveOpen = ref(false)
const receiveError = ref<unknown>(null)
const receiveLines = ref<ReceiveLine[]>([])

const putawayOpen = ref(false)
const putawayError = ref<unknown>(null)
const putawayTarget = ref(0)
const putawayTask = ref<PutawayDetail | null>(null)

const asnId = computed(() => Number(route.params.id))
const asn = computed(() => detail.value?.asn ?? null)

const receiveErrorText = computed(() => {
  if (!receiveLines.value.length) return '没有可收货的明细'
  for (const [i, line] of receiveLines.value.entries()) {
    if (!(line.qty > 0)) continue // 0 表示这行本次不收
    if (line.qty > line.remaining) return `第 ${i + 1} 行不能超过待收数量 ${line.remaining}`
    if (line.batch_managed && !line.batch_no.trim()) return `第 ${i + 1} 行商品按批次管理，必须填批次号`
    if (!line.location_id) return `第 ${i + 1} 行请选择收货库位`
  }
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    detail.value = await getAsn(asnId.value)
    notice.value = noticeText
  } catch (e) {
    error.value = e
    detail.value = null
  } finally {
    loading.value = false
  }
}

async function loadLocations(): Promise<void> {
  const wh = detail.value?.asn.warehouse_id
  if (!wh) return
  try {
    locations.value = (await listLocations({ page: 1, size: 200, warehouse: wh })).list
  } catch {
    // 库位拿不到时收货弹窗会提示选择失败，列表本身不受影响
  }
}

onMounted(() => {
  void load()
})

function back(): void {
  void router.push({ name: 'inbound' })
}

async function act(action: 'release' | 'cancel' | 'close', text: string): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    if (action === 'release') await releaseAsn(asnId.value)
    else if (action === 'cancel') await cancelAsn(asnId.value)
    else await closeAsn(asnId.value)
    await load(text)
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

function openReceive(): void {
  if (!detail.value) return
  receiveLines.value = detail.value.items.map((item) => ({
    asn_item_id: item.id,
    sku_label: `${item.sku_code} ${item.sku_name}`.trim(),
    unit: item.unit,
    batch_managed: item.batch_managed,
    remaining: item.expected_qty - item.received_qty,
    qty: Math.max(item.expected_qty - item.received_qty, 0),
    batch_no: '',
    production_date: null,
    expiry_date: null,
    location_id: locations.value.find((l) => l.kind === 'staging')?.id ?? locations.value[0]?.id ?? 0,
  }))
  receiveError.value = null
  receiveOpen.value = true
}

async function submitReceive(): Promise<void> {
  if (receiveErrorText.value) return
  busy.value = true
  receiveError.value = null
  try {
    await receiveAsn(asnId.value, {
      items: receiveLines.value
        .filter((line) => line.qty > 0)
        .map((line) => ({
          asn_item_id: line.asn_item_id,
          qty: Number(line.qty),
          batch_no: line.batch_managed ? line.batch_no.trim() : '',
          production_date: line.production_date,
          expiry_date: line.expiry_date,
          location_id: line.location_id,
        })),
    })
    receiveOpen.value = false
    await load('收货已记录，库存已落入收货位')
    await loadLocations()
  } catch (e) {
    receiveError.value = e
  } finally {
    busy.value = false
  }
}

function openPutaway(task: PutawayDetail): void {
  putawayTask.value = task
  putawayTarget.value =
    locations.value.find((l) => l.id !== task.from_location_id && l.kind === 'shelf')?.id ??
    locations.value.find((l) => l.id !== task.from_location_id)?.id ??
    0
  putawayError.value = null
  putawayOpen.value = true
}

async function submitPutaway(): Promise<void> {
  if (!putawayTask.value || !putawayTarget.value) return
  busy.value = true
  putawayError.value = null
  try {
    await confirmPutaway(putawayTask.value.id, putawayTarget.value)
    putawayOpen.value = false
    await load('上架已完成')
  } catch (e) {
    putawayError.value = e
  } finally {
    busy.value = false
  }
}

const canEdit = computed(() => asn.value?.status === 'draft')
const canCancel = computed(() =>
  asn.value ? asn.value.status === 'draft' || asn.value.status === 'released' : false,
)
const canReceive = computed(() =>
  asn.value ? asn.value.status === 'released' || asn.value.status === 'receiving' : false,
)
const canClose = computed(() =>
  asn.value ? asn.value.status === 'receiving' || asn.value.status === 'received' : false,
)

/** 待收数量合计：全部收完就不用再收了 */
const remainingTotal = computed(() =>
  (detail.value?.items ?? []).reduce((sum, item) => sum + (item.expected_qty - item.received_qty), 0),
)
</script>

<template>
  <div class="yc-inbd" v-loading="loading">
    <header class="yc-inbd__bar">
      <el-button @click="back">返回列表</el-button>
      <template v-if="asn">
        <span class="yc-inbd__docno">{{ asn.doc_no }}</span>
        <el-tag :type="STATUS_TAG[asn.status as InboundStatus]" size="small">
          {{ STATUS_LABELS[asn.status as InboundStatus] ?? asn.status }}
        </el-tag>
        <div class="yc-inbd__spacer" />
        <el-button
          v-perm="'wms:inbound:release'"
          v-if="canEdit"
          :loading="busy"
          type="primary"
          @click="act('release', '入库单已释放，可以开始收货')"
        >
          释放
        </el-button>
        <el-button v-perm="'wms:inbound:receive'" v-if="canReceive" type="primary" @click="openReceive">
          收货
        </el-button>
        <el-button
          v-perm="'wms:inbound:close'"
          v-if="canClose"
          :loading="busy"
          @click="act('close', '入库单已关闭')"
        >
          关闭
        </el-button>
        <el-popconfirm
          v-if="canCancel"
          title="确认取消该入库单？"
          width="200"
          @confirm="act('cancel', '入库单已取消')"
        >
          <template #reference>
            <el-button v-perm="'wms:inbound:cancel'" type="danger">取消</el-button>
          </template>
        </el-popconfirm>
      </template>
    </header>

    <div v-if="error" class="yc-inbd__error">
      <TraceIdAlert :error="error" />
    </div>
    <div v-if="notice" class="yc-inbd__notice">{{ notice }}</div>

    <template v-if="detail">
      <el-descriptions class="yc-inbd__meta" :column="4" border>
        <el-descriptions-item label="仓库">{{ detail.warehouse_name || `#${asn!.warehouse_id}` }}</el-descriptions-item>
        <el-descriptions-item label="供应商">
          {{ detail.supplier_name || `#${asn!.supplier_id}` }}
        </el-descriptions-item>
        <el-descriptions-item label="期望到货">
          {{ asn!.expected_at ? formatDateTime(asn!.expected_at) : '—' }}
        </el-descriptions-item>
        <el-descriptions-item label="创建人">{{ asn!.created_by }}</el-descriptions-item>
        <el-descriptions-item label="创建时间" :span="2">{{ formatDateTime(asn!.created_at) }}</el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ asn!.remark || '—' }}</el-descriptions-item>
      </el-descriptions>

      <el-card class="yc-inbd__card">
        <template #header>
          <span>入库明细</span>
          <span v-if="remainingTotal > 0" class="yc-inbd__hint">待收 {{ remainingTotal }}</span>
        </template>
        <el-table :data="detail.items" row-key="id">
          <el-table-column prop="sku_code" label="商品编码" width="140" />
          <el-table-column prop="sku_name" label="商品名称" min-width="160" show-overflow-tooltip />
          <el-table-column prop="unit" label="单位" width="80" />
          <el-table-column label="批次管理" width="96">
            <template #default="{ row }">
              <el-tag v-if="row.batch_managed" size="small">是</el-tag>
              <span v-else>否</span>
            </template>
          </el-table-column>
          <el-table-column prop="expected_qty" label="应收" width="90" />
          <el-table-column prop="received_qty" label="已收" width="90" />
          <el-table-column label="待收" width="90">
            <template #default="{ row }">
              <span :class="{ 'yc-inbd__pending': row.expected_qty > row.received_qty }">
                {{ row.expected_qty - row.received_qty }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <el-card class="yc-inbd__card">
        <template #header>
          <span>上架任务</span>
          <span class="yc-inbd__hint">收货落暂存位后自动生成，确认目标货架位即完成上架</span>
        </template>
        <el-table v-if="detail.putaways.length" :data="detail.putaways" row-key="id">
          <el-table-column prop="task_no" label="任务号" width="170" />
          <el-table-column prop="sku_code" label="商品编码" width="140" />
          <el-table-column prop="sku_name" label="商品名称" min-width="150" show-overflow-tooltip />
          <el-table-column prop="batch_no" label="批次" width="120" />
          <el-table-column prop="qty" label="数量" width="90" />
          <el-table-column prop="from_location_code" label="来源位" width="120" />
          <el-table-column label="目标位" min-width="120">
            <template #default="{ row }">{{ row.to_location_code || '—' }}</template>
          </el-table-column>
          <el-table-column label="状态" width="96">
            <template #default="{ row }">
              <el-tag :type="row.status === 'done' ? 'success' : 'warning'" size="small">
                {{ PUTAWAY_STATUS_LABELS[row.status as PutawayStatus] ?? row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="110" fixed="right">
            <template #default="{ row }">
              <el-button
                v-if="row.status === 'pending'"
                v-perm="'wms:inbound:putaway'"
                link
                type="primary"
                @click="openPutaway(row)"
              >
                确认上架
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <EmptyState v-else title="还没有上架任务" description="收货之后会自动生成上架任务。" />
      </el-card>
    </template>

    <el-dialog
      v-if="receiveOpen"
      v-model="receiveOpen"
      title="收货"
      width="860px"
      :close-on-click-modal="false"
    >
      <div v-if="receiveError" class="yc-inbd__error">
        <TraceIdAlert :error="receiveError" />
      </div>

      <el-form label-position="top" @submit.prevent="submitReceive">
        <el-table :data="receiveLines">
          <el-table-column prop="sku_label" label="商品" min-width="170" show-overflow-tooltip />
          <el-table-column label="待收" width="80">
            <template #default="{ row }">{{ row.remaining }} {{ row.unit }}</template>
          </el-table-column>
          <el-table-column label="本次收货" width="150">
            <template #default="{ row }">
              <el-input-number v-model="row.qty" :min="0" :max="row.remaining" controls-position="right" />
            </template>
          </el-table-column>
          <el-table-column label="批次号" width="150">
            <template #default="{ row }">
              <el-input
                v-if="row.batch_managed"
                v-model="row.batch_no"
                maxlength="32"
                placeholder="必填"
              />
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column label="生产日期" width="160">
            <template #default="{ row }">
              <el-date-picker
                v-model="row.production_date"
                type="date"
                value-format="YYYY-MM-DD"
                placeholder="可留空"
              />
            </template>
          </el-table-column>
          <el-table-column label="有效期至" width="160">
            <template #default="{ row }">
              <el-date-picker
                v-model="row.expiry_date"
                type="date"
                value-format="YYYY-MM-DD"
                placeholder="留空按保质期推算"
              />
            </template>
          </el-table-column>
          <el-table-column label="收货库位" width="170">
            <template #default="{ row }">
              <el-select v-model="row.location_id" filterable placeholder="选择库位">
                <el-option v-for="l in locations" :key="l.id" :label="l.code" :value="l.id" />
              </el-select>
            </template>
          </el-table-column>
        </el-table>

        <div class="yc-inbd__actions">
          <el-button type="primary" native-type="submit" :loading="busy" :disabled="!!receiveErrorText">
            确认收货
          </el-button>
          <span v-if="receiveErrorText" class="yc-inbd__hint">{{ receiveErrorText }}</span>
        </div>
      </el-form>

      <template #footer>
        <el-button @click="receiveOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-if="putawayOpen"
      v-model="putawayOpen"
      :title="`确认上架 · ${putawayTask?.task_no ?? ''}`"
      width="480px"
      :close-on-click-modal="false"
    >
      <div v-if="putawayError" class="yc-inbd__error">
        <TraceIdAlert :error="putawayError" />
      </div>

      <el-form label-position="top" @submit.prevent="submitPutaway">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="商品">
            {{ putawayTask?.sku_code }} {{ putawayTask?.sku_name }}
          </el-descriptions-item>
          <el-descriptions-item label="批次">{{ putawayTask?.batch_no || '—' }}</el-descriptions-item>
          <el-descriptions-item label="数量">{{ putawayTask?.qty }}</el-descriptions-item>
          <el-descriptions-item label="来源位">{{ putawayTask?.from_location_code }}</el-descriptions-item>
        </el-descriptions>

        <el-form-item class="yc-inbd__target" label="目标货架位">
          <el-select v-model="putawayTarget" filterable placeholder="选择货架位">
            <el-option
              v-for="l in locations.filter((item) => item.id !== putawayTask?.from_location_id)"
              :key="l.id"
              :label="`${l.code} ${l.name}`.trim()"
              :value="l.id"
            />
          </el-select>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="busy" :disabled="!putawayTarget">
          确认上架
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="putawayOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-inbd {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-3);
  }

  &__docno {
    font-weight: 600;
  }

  &__spacer {
    flex: 1;
  }

  &__meta {
    margin-bottom: var(--yc-space-4);
  }

  &__card {
    margin-bottom: var(--yc-space-4);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__notice {
    margin-bottom: var(--yc-space-3);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    margin-left: var(--yc-space-2);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__pending {
    color: var(--el-color-warning);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--yc-space-3);
    margin-top: var(--yc-space-3);
  }

  &__target {
    margin-top: var(--yc-space-3);
  }
}
</style>
